// react
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// utils
import { reportInviteError } from 'utils/inviteError';

// components
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';
import { Editor } from '@karehero/llama/components/organisms/Editor/Editor';
import { Switch } from '@karehero/llama/components/atoms/Switch/Switch';
import { createFieldHelper } from '@karehero/llama/components/organisms/EditorFields/fieldHelper';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import {
  useAssociateKindeAccountWithInviteMutation,
  useGetAllInvitedMemberQuery,
  useUpdateInviteAwaitingFeeMutation,
} from 'store/api/invitedMember';
import { useReinviteMemberMutation } from 'store/api/organization';
import { FieldType, InvitedMember } from '@karehero/models';
import { newToast } from 'store/slices/uiSlice';
import { useDispatch } from 'react-redux';

// consts
const KINDE_USER_LENGTH = 35;

// columns
const columns: GridColDef[] = [
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
  { field: 'firstName', headerName: 'First Name', flex: 0.7, minWidth: 120 },
  {
    field: 'organizationId',
    headerName: 'Organisation ID',
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row;
      return (
        <Link to={`/admin/organization/${row.organizationId}/member`}>
          {row.organizationId}
        </Link>
      );
    },
  },
];

const kindeDetailsFields = createFieldHelper<{
  kindeID: string;
  inviteID: string;
  inviteEmail: string;
  organization: string;
}>({
  inviteID: {
    label: 'Invite ID',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
  inviteEmail: {
    label: 'Invite Email',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
  kindeID: {
    label: 'Kinde ID',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  organization: {
    label: 'Organisation',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
});

/**
 * ManageInvitedMembers shows the invited members.
 */
export const ManageInvitedMembers = () => {
  // state
  const [isShowAssociateKindeAccount, setIsShowAssociateKindeAccount] =
    useState(false);
  const [selectedProfileID, setSelectedProfileID] = useState<string | null>(
    null,
  );
  const [filterKareHeroEmail, setFilterKareHeroEmail] = useState(false);
  const [kindeDetails, setKindeDetails] = useState<
    Partial<{ kindeID: string; inviteEmail: string; inviteID: string }>
  >({});

  // hooks
  const { data: invitedMembers } = useGetAllInvitedMemberQuery();
  const [reinviteMember] = useReinviteMemberMutation();
  const [updateInviteAwaitingFee] = useUpdateInviteAwaitingFeeMutation();
  const [associateKindeAccount] = useAssociateKindeAccountWithInviteMutation();
  const dispatch = useDispatch();

  // memos
  const selectedProfile = useMemo(() => {
    if (!selectedProfileID || !invitedMembers) return null;
    return invitedMembers.find(
      (invitedMember) => invitedMember.id === selectedProfileID,
    );
  }, [selectedProfileID, invitedMembers]);

  const rows = useMemo(() => {
    if (!invitedMembers) return [];
    return invitedMembers
      .filter((invitedMember) => {
        if (!filterKareHeroEmail) return true;
        return !invitedMember.email.toLowerCase().includes('@karehero.com');
      })
      .map((invitedMember) => ({
        ...invitedMember,
      }));
  }, [invitedMembers, filterKareHeroEmail]);

  // methods
  const handleReinviteMember = useCallback(
    async (member: InvitedMember) => {
      if (!member?.id) return;
      try {
        await reinviteMember(member.id).unwrap();
        dispatch(
          newToast({
            title: 'Invitation Success',
            description: `${!member.inviteSentAt ? 'Sent' : 'Resent'} invitation to ${member.email}`,
          }),
        );
      } catch (e) {
        reportInviteError(e);
      }
    },
    [dispatch, reinviteMember],
  );

  const handleUpdateAwaitingFee = useCallback(
    async (member: InvitedMember) => {
      if (!member) return;

      const res = await updateInviteAwaitingFee(member);
      if ('error' in res) {
        console.error(res.error);
        return;
      }
      dispatch(
        newToast({
          title: 'Fee Updated',
          description: `Sent invitation to ${member.email} using invite button`,
          type: 'success',
        }),
      );
    },
    [dispatch, updateInviteAwaitingFee],
  );

  const handleAssociateKindeAccount = useCallback(
    async (profile: InvitedMember, kindeID: string) => {
      const res = await associateKindeAccount({
        inviteID: profile.id,
        kindeID,
      });
      if ('error' in res) {
        console.error(res.error);
        return;
      }
      dispatch(
        newToast({
          title: 'Account Linked Successfully',
          description: `Associated invite with kinde account and created new account successfully`,
          type: 'success',
        }),
      );

      setKindeDetails({});
      setIsShowAssociateKindeAccount(false);
    },
    [dispatch, associateKindeAccount],
  );

  return (
    <AdminTablePage title='Invited Members'>
      <DataTable
        columns={columns}
        rows={rows}
        rowsPerPage={10}
        actions={[
          {
            label: 'Reinvite',
            icon: 'envelope',
            onPress: (invitedMember: InvitedMember) =>
              handleReinviteMember(invitedMember),
          },
          {
            label: 'Mark Fee Paid',
            icon: 'money-bills',
            onPress: (invitedMember: InvitedMember) =>
              handleUpdateAwaitingFee(invitedMember),
            isDisabled: (row: any) => !row.isAwaitingFee,
          },
          {
            label: 'Associate Kinde Account',
            icon: 'user-plus',
            onPress: (invitedMember: InvitedMember) => {
              setSelectedProfileID(invitedMember.id);
              setKindeDetails({
                inviteID: invitedMember.id,
                inviteEmail: invitedMember.email,
              });
              setIsShowAssociateKindeAccount(true);
            },
          },
        ]}
        toolbarActions={
          <Switch
            id='filterKareHeroEmail'
            value={filterKareHeroEmail}
            onChange={setFilterKareHeroEmail}
            label='Filter KareHero Emails'
            size='sm'
            isNoWrap
          />
        }
      />
      <Dialog
        title='Associate Kinde Account with Invite'
        variant='sheet'
        isOpen={isShowAssociateKindeAccount}
        setIsOpen={setIsShowAssociateKindeAccount}
        zIndex={1}
      >
        <Editor
          onSave={() => {
            if (!selectedProfile) return;
            if (!kindeDetails.kindeID || !kindeDetails.inviteID) return;
            if (selectedProfile.id !== kindeDetails.inviteID) return;

            handleAssociateKindeAccount(selectedProfile, kindeDetails.kindeID);
          }}
          onCancel={() => setIsShowAssociateKindeAccount(false)}
          saveLabel='Save'
          cancelLabel='Cancel'
          isWithArrows={false}
          isActionsDisabled={false}
          value={{ ...kindeDetails, inviteID: selectedProfile?.id }}
          onChange={(val: any) =>
            setKindeDetails({ ...kindeDetails, kindeID: val.kindeID })
          }
          fields={kindeDetailsFields}
          isDisabled={!selectedProfile}
          isFloatingActions={true}
          isForm={true}
          isSaveDisabled={
            !kindeDetails.kindeID ||
            kindeDetails.kindeID.length !== KINDE_USER_LENGTH
          }
        />
      </Dialog>
    </AdminTablePage>
  );
};

export default ManageInvitedMembers;
